/**
 * 气泡位置计算工具函数（仅水平方向）
 */

export type TPlacement = 'left' | 'right';

export interface IPlacementOptions {
  /** 预估气泡宽度 */
  bubbleWidth?: number;
  /** 预留边距 */
  margin?: number;
}

/**
 * 计算气泡的最佳位置（仅水平方向）
 * @param element 目标元素
 * @param options 配置选项
 * @returns 最佳位置
 */
export const calculateOptimalPlacement = (
  element: HTMLElement,
  options: IPlacementOptions = {}
): TPlacement => {
  const {
    bubbleWidth = 314,
    margin = 20
  } = options;

  const rect = element.getBoundingClientRect();
  const viewportWidth = window.innerWidth;

  // 计算元素中心点
  const elementCenterX = rect.left + rect.width / 2;

  // 优先选择右侧，如果空间不足则选择左侧
  if (elementCenterX < viewportWidth / 2) {
    // 元素在左半部分，优先显示在右侧
    const rightSpace = viewportWidth - rect.right - margin;
    return rightSpace >= bubbleWidth ? 'right' : 'left';
  }
  
  // 元素在右半部分，优先显示在左侧
  const leftSpace = rect.left - margin;
  return leftSpace >= bubbleWidth ? 'left' : 'right';
};

/**
 * 检查指定位置是否有足够空间（仅水平方向）
 * @param element 目标元素
 * @param placement 位置
 * @param options 配置选项
 * @returns 是否有足够空间
 */
export const hasEnoughSpace = (
  element: HTMLElement,
  placement: TPlacement,
  options: IPlacementOptions = {}
): boolean => {
  const {
    bubbleWidth = 314,
    margin = 20
  } = options;

  const rect = element.getBoundingClientRect();
  const viewportWidth = window.innerWidth;

  switch (placement) {
    case 'left':
      return rect.left - margin >= bubbleWidth;
    case 'right':
      return viewportWidth - rect.right - margin >= bubbleWidth;
    default:
      return false;
  }
};

/**
 * 获取所有可用位置（按优先级排序，仅水平方向）
 * @param element 目标元素
 * @param options 配置选项
 * @returns 可用位置数组
 */
export const getAvailablePlacements = (
  element: HTMLElement,
  options: IPlacementOptions = {}
): TPlacement[] => {
  const placements: TPlacement[] = ['left', 'right'];
  
  return placements.filter(placement => hasEnoughSpace(element, placement, options));
};
